// Fractional indexing — generates compact, lexicographically-sortable string
// keys that allow inserting an item *between* any two existing items without
// renumbering the others. This is the conflict-free ordering primitive behind
// `childIds`: instead of storing an array (whose concurrent moves duplicate
// elements on a Yjs Y.Array), each entity carries an `order` string and a
// parent's children are derived by sorting siblings on `(order, id)`.
//
// A "move" becomes a single idempotent `set(order, keyBetween(prev, next))`:
//   - concurrent moves of the *same* element → last-writer-wins on one key,
//     so the element appears exactly once (never duplicated);
//   - concurrent inserts into the *same* slot → identical keys, broken
//     deterministically by entity id, so every peer converges on one order.
//
// Algorithm: the bounded-(0,1) midpoint scheme (David Greenspan / rocicorp
// `fractional-indexing`, MIT). Keys are digit strings interpreted as the
// fraction 0.<digits> in base-`DIGITS.length`. The invariant "a key never ends
// in the lowest digit" keeps every key well-formed and every midpoint finite.

const DIGITS = "0123456789abcdefghijklmnopqrstuvwxyz";
const ZERO = DIGITS[0];

// Returns a digit string strictly between `a` and `b` under lexicographic
// order. `a === null` means negative infinity (0.000…); `b === null` means
// positive infinity (1.000…). Requires `a < b` when both are provided.
const midpoint = (a: string, b: string | null): string => {
  if (b !== null && a >= b) {
    throw new Error(`fractional-index: lower bound ${a} >= upper bound ${b}`);
  }
  if (a.slice(-1) === ZERO || (b && b.slice(-1) === ZERO)) {
    throw new Error("fractional-index: bounds must not end in the lowest digit");
  }

  if (b) {
    // Copy the common prefix verbatim, then recurse on the divergent suffix.
    let n = 0;
    while ((a[n] || ZERO) === b[n]) n += 1;
    if (n > 0) return b.slice(0, n) + midpoint(a.slice(n), b.slice(n));
  }

  const digitA = a ? DIGITS.indexOf(a[0]) : 0;
  const digitB = b !== null ? DIGITS.indexOf(b[0]) : DIGITS.length;

  if (digitB - digitA > 1) {
    // There is room for a digit strictly between the two leading digits.
    const mid = Math.round(0.5 * (digitA + digitB));
    return DIGITS[mid];
  }

  // Leading digits are consecutive — descend a level.
  if (b && b.length > 1) return b.slice(0, 1);
  return DIGITS[digitA] + midpoint(a.slice(1), null);
};

export const generateKeyBetween = (
  a: string | null,
  b: string | null,
): string => {
  if (a !== null && b !== null && a >= b) {
    throw new Error(`fractional-index: ${a} >= ${b}`);
  }
  return midpoint(a ?? "", b);
};

// Generates `n` keys strictly between `a` and `b`, evenly distributed, each
// strictly ordered. Used to seed an existing ordered list (e.g. a parent's
// children) with `generateNKeysBetween(null, null, count)`.
export const generateNKeysBetween = (
  a: string | null,
  b: string | null,
  n: number,
): string[] => {
  if (n <= 0) return [];
  if (n === 1) return [generateKeyBetween(a, b)];

  if (b === null) {
    let prev = generateKeyBetween(a, null);
    const result = [prev];
    for (let i = 1; i < n; i += 1) {
      prev = generateKeyBetween(prev, null);
      result.push(prev);
    }
    return result;
  }

  if (a === null) {
    let next = generateKeyBetween(null, b);
    const result = [next];
    for (let i = 1; i < n; i += 1) {
      next = generateKeyBetween(null, next);
      result.unshift(next);
    }
    return result;
  }

  const mid = Math.floor(n / 2);
  const center = generateKeyBetween(a, b);
  return [
    ...generateNKeysBetween(a, center, mid),
    center,
    ...generateNKeysBetween(center, b, n - mid - 1),
  ];
};
